/**
 * Claim Token API Client
 *
 * Claim links: each link carries a token bound to one specific edition copy.
 * See docs/features/LAUNCH_CLAIM_TOKENS.md
 */

import { apiClient, ApiResponse } from './client';

export type ClaimReasonCode =
  | 'already_claimed'
  | 'voided'
  | 'launch_not_active'
  | 'not_yet_ready'
  | 'item_unavailable';

export interface ClaimLookup {
  valid: boolean;
  claimable: boolean;
  /** True when the signed-in viewer is the person who already claimed this card */
  claimedByViewer: boolean;
  reason: string | null;
  reasonCode: ClaimReasonCode | null;
  launch: {
    name: string;
    slug: string;
    collection_slug: string | null;
  };
  piece: {
    name: string | null;
    edition_name: string | null;
    image_url: string | null;
    serial: number | null;
    max_supply: number | null;
    nft_token_id: string | null;
  };
}

export interface RedeemResult {
  success: boolean;
  alreadyClaimed: boolean;
  message: string;
  data?: {
    mint_id?: string;
    serial?: number | null;
    piece_name?: string | null;
    nft_token_id?: string | null;
  };
}

/**
 * Read-only lookup. Safe to call unauthenticated — this is what a visitor
 * opening the link hits before they have signed in.
 */
export async function getClaim(token: string): Promise<ClaimLookup> {
  const response = await apiClient.get<ApiResponse<ClaimLookup>>(
    `/claims/${encodeURIComponent(token)}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to look up claim');
  }

  return response.data;
}

/**
 * Redeem the card. Requires authentication — the transfer offer needs a
 * destination wallet.
 */
export async function redeemClaim(token: string): Promise<RedeemResult> {
  return apiClient.post<RedeemResult>(`/claims/${encodeURIComponent(token)}/redeem`);
}
